/**
 * Publish gate (Sprint 4.3.4, PROD-653).
 *
 * Pure helper consumed by `<PathwayPublishFlow>` and the
 * `pathway-publish-flow` test suite. Refuses to publish when any of
 * the following hold (HC8 fail-closed):
 *
 *   1. Open drafts exist for any component instance.
 *   2. Sara-test passes < 28/28.
 *   3. A pathway cites a component id that has been deleted.
 *
 * The function returns a discriminated union so the calling UI can
 * surface a structured message rather than a generic boolean.
 *
 * HC5: zero em / en dashes.
 */
import {
  CARE_FLOW_FOLDERS,
  type CareFlowComponentBundle,
} from './types';

export type PublishGateReason =
  | 'open-drafts'
  | 'sara-fail'
  | 'deleted-cited-component';

export interface PublishGateBlocked {
  ok: false;
  reasons: ReadonlyArray<PublishGateReason>;
  details: string;
}

export interface PublishGatePassed {
  ok: true;
}

export type PublishGateResult = PublishGateBlocked | PublishGatePassed;

export interface PublishGateInput {
  openDraftCount: number;
  saraPassCount: number;
  saraTotalCount: number;
  bundle: CareFlowComponentBundle;
  citedComponentIds: ReadonlyArray<string>;
}

export function evaluatePublishGate(input: PublishGateInput): PublishGateResult {
  const reasons: PublishGateReason[] = [];
  const details: string[] = [];

  if (input.openDraftCount > 0) {
    reasons.push('open-drafts');
    details.push(
      `${input.openDraftCount} unsaved draft${input.openDraftCount === 1 ? '' : 's'} blocking publish.`,
    );
  }

  if (input.saraPassCount < input.saraTotalCount) {
    reasons.push('sara-fail');
    details.push(
      `Sara-test ${input.saraPassCount} of ${input.saraTotalCount} blocking publish.`,
    );
  }

  const presentIds = new Set<string>();
  for (const folder of CARE_FLOW_FOLDERS) {
    for (const component of input.bundle[folder]) {
      presentIds.add((component as { id: string }).id);
    }
  }
  const missing = input.citedComponentIds.filter((id) => !presentIds.has(id));
  if (missing.length > 0) {
    reasons.push('deleted-cited-component');
    details.push(
      `${missing.length} cited component${missing.length === 1 ? '' : 's'} missing from bundle: ${missing.join(', ')}`,
    );
  }

  if (reasons.length === 0) {
    return { ok: true };
  }
  return {
    ok: false,
    reasons,
    details: details.join(' '),
  };
}
